import { useAuthStore } from '@goodneighbor/auth/hooks';
import type { AxiosResponse } from 'axios';
import {
  RegisterEventPayload,
  CheckFormPayload,
  ForgotPasswordPayload,
  ForgotPasswordVerifyOtpPayload,
  ForgotPasswordResetPayload,
  DeleteAccountResponse
} from './queries';
import axios from 'axios';

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
const isDevelopment = process.env.NODE_ENV === 'development';
// @ts-ignore
const nextPublicServerBaseURL = process.env.NEXT_PUBLIC_SERVER_BASE_URL;
// @ts-ignore
const expoPublicServerBaseURL = process.env.EXPO_PUBLIC_SERVER_BASE_URL;
// @ts-ignore
const expoPublicEventsBaseURL = process.env.EXPO_PUBLIC_EVENTS_BASE_URL;
/* eslint-enable @typescript-eslint/ban-ts-comment */

const createInstance = () => {
  const { accessToken } = useAuthStore.getState();

  const instance = axios.create({
    baseURL: nextPublicServerBaseURL + '/api/v1.0',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (isDevelopment) {
    instance.interceptors.request.use((config) => {
      const fullUrl = config.baseURL ? config.baseURL + config.url : config.url;
      /* eslint-disable no-console */
      console.log(
        'Microservice API Request:',
        config.method?.toUpperCase(),
        fullUrl
      );
      /* eslint-enable no-console */
      return config;
    });
  }

  return instance;
};

type InstanceProxy = {
  isV2?: boolean;
} | null;

// TODO: We need to remove this soon, as we will use one instance for both web and mobile
const createInstanceProxy = (config?: InstanceProxy) => {
  const { accessToken } = useAuthStore.getState();
  const { isV2 = false } = config || {};
  const apiVersion = isV2 ? '/api/v2.0' : '/api/v1.0';
  const instance = axios.create({
    baseURL: `${expoPublicServerBaseURL}${apiVersion}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (isDevelopment) {
    instance.interceptors.request.use((config) => {
      const fullUrl = config.baseURL ? config.baseURL + config.url : config.url;
      /* eslint-disable no-console */
      console.log(
        'Microservice Proxy API Request:',
        config.method?.toUpperCase(),
        fullUrl
      );
      if (config.params) {
        console.log('Query Params:', JSON.stringify(config.params, null, 2));
        const queryString = new URLSearchParams(config.params).toString();
        console.log('Full URL:', `${fullUrl}?${queryString}`);
      }
      /* eslint-enable no-console */
      return config;
    });
  }

  return instance;
};

const createEventsInstanceProxy = () => {
  const { accessToken } = useAuthStore.getState();
  const baseURL =
    expoPublicEventsBaseURL || `${expoPublicServerBaseURL}/api/v1.0`;

  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (isDevelopment) {
    instance.interceptors.request.use((config) => {
      const fullUrl = config.baseURL ? config.baseURL + config.url : config.url;
      /* eslint-disable no-console */
      console.log(
        'Microservice Events API Request:',
        config.method?.toUpperCase(),
        fullUrl
      );
      if (config.params) {
        console.log('Query Params:', JSON.stringify(config.params, null, 2));
        const queryString = new URLSearchParams(config.params).toString();
        console.log('Full URL:', `${fullUrl}?${queryString}`);
      }
      /* eslint-enable no-console */
      return config;
    });
  }

  return instance;
};

export const getAdminEvents = async <T>(query: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  q?: string;
}): Promise<{ data: T }> => {
  const instance = createInstance();
  const response = await instance.get('/admin/events', {
    params: query
  });
  return response.data;
};

export const updateEvent = async (
  eventId: string,
  data: {
    isApproved: number | null;
    startsAt?: string;
    endsAt?: string;
    title?: string;
    description?: string;
    locationName?: string;
    eventLocation?: {
      formattedAddress: string;
      placeId?: string;
      addressComponents?: Array<{
        longName: string;
        shortName: string;
        types: string[];
      }>;
      geometry?: {
        location: {
          lat: number;
          lng: number;
        };
        locationType?: string;
        viewport?: {
          northeast: {
            lat: number;
            lng: number;
          };
          southwest: {
            lat: number;
            lng: number;
          };
        };
      };
    };
  }
): Promise<void> => {
  const instance = createInstance();
  await instance.patch(`/admin/events/${eventId}`, {
    isApproved: data.isApproved === null ? null : Boolean(data.isApproved),
    startsAt: data.startsAt,
    endsAt: data.endsAt,
    title: data.title,
    description: data.description,
    locationName: data.locationName,
    eventLocation: data.eventLocation
  });
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  const instance = createInstance();
  await instance.delete(`/admin/events/${eventId}`);
};

type UpdateOrganizationData = {
  status?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
};

export const updateOrganizationDetails = async (
  organizationId: string,
  data: Partial<UpdateOrganizationData>
): Promise<void> => {
  const instance = createInstance();
  await instance.patch(`/admin/organizations/${organizationId}`, data);
};

// TODO: We need to remove
// --------------- For Mobile ----------------

export const signInEmailPassword = async <T>(
  email: string,
  password?: string
): Promise<T> => {
  const instance = createInstanceProxy();
  const response = await instance.post<T>('/auth/sign-in', {
    phoneOrEmail: email,
    password
  });

  return response.data;
};

export const getEventsNew = async <T>(query: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  location?: string;
  q?: string;
  north?: number;
  south?: number;
  east?: number;
  west?: number;
  organization?: string;
  sponsor?: string;
  startDate?: string;
  endDate?: string;
  volunteersMax?: number;
  volunteersMin?: number;
  radius?: number;
  causeTypes?: string;
  activityTypes?: string;
  skillTypes?: string;
}): Promise<{ data: T }> => {
  const instance = createEventsInstanceProxy();

  const formattedParams = {
    ...query,
    north: query.north?.toString(),
    south: query.south?.toString(),
    east: query.east?.toString(),
    west: query.west?.toString(),
    page: query.page?.toString(),
    perPage: query.perPage ? Math.min(query.perPage, 18) : 18
  };

  const cleanParams = Object.fromEntries(
    Object.entries(formattedParams).filter(
      ([_, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== 'undefined'
    )
  );

  const response = await instance.get(
    expoPublicEventsBaseURL ? '/' : '/events',
    {
      params: cleanParams
    }
  );
  return response.data;
};

export const getEventByIdNew = async <T>(
  id: string | number
): Promise<{ data: T }> => {
  const instance = createInstanceProxy();
  const response = await instance.get<{ data: T }>(`/events/${id}`);
  return response.data;
};

export const getEventAttendanceCount = async <T>(
  id: string | number
): Promise<{ data: T }> => {
  const instance = createInstanceProxy();
  const response = await instance.get<{ data: T }>(
    `/events/${id}/register/count`
  );
  return response.data;
};

export const postRegisterEventNew = async <T>(
  id: string | number,
  body: RegisterEventPayload
): Promise<{ data: T }> => {
  const instance = createInstanceProxy();
  const response = await instance.post<{ data: T }>(
    `/events/${id}/register`,
    body
  );
  return response.data;
};

export const postCheckEmail = async (
  body: CheckFormPayload
): Promise<{
  exists: boolean;
  message?: string;
}> => {
  const instance = createInstanceProxy();
  const { data } = await instance.post<{
    exists: boolean;
    message?: string;
  }>(`/auth/check-email-phone`, body);
  return data;
};

export const postForgotPassword = async (
  body: ForgotPasswordPayload
): Promise<{ status: number; data: unknown }> => {
  const instance = createInstanceProxy();
  const response = await instance.post(`/auth/forgot`, body);
  return { status: response.status, data: response.data };
};

export const postForgotPasswordVerifyOtp = async ({
  email,
  otp
}: ForgotPasswordVerifyOtpPayload): Promise<{
  status: number;
  data: unknown;
}> => {
  const instance = createInstanceProxy();
  const response: AxiosResponse = await instance.post(
    `/auth/reset/otp/${encodeURIComponent(email)}/${encodeURIComponent(otp)}`
  );
  return { status: response.status, data: response.data };
};

export const postForgotPasswordReset = async ({
  email,
  token,
  password
}: ForgotPasswordResetPayload): Promise<{ status: number; data: unknown }> => {
  const instance = createInstanceProxy();
  const response: AxiosResponse = await instance.post(
    `/auth/reset/${encodeURIComponent(email)}/${encodeURIComponent(token)}`,
    { password }
  );
  return { status: response.status, data: response.data };
};

export const signUpEmailPassword = async <T>(payload: {
  profile: {
    email: string;
    firstName: string;
    lastName: string;
  };
  phone: string;
  password: string;
}): Promise<T> => {
  const instance = createInstanceProxy();
  const { data } = await instance.post<T>('/auth/signup', payload);
  return data;
};

export const sendVerifiedCode = async <T>(payload: {
  profile: {
    email: string;
    firstName: string;
    lastName: string;
  };
  phone: string;
  password: string;
}): Promise<T> => {
  const instance = createInstanceProxy({
    isV2: true
  });
  const { data } = await instance.post<T>('/auth/signup/verify', payload);
  return data;
};

export const verifySignup = async <T>(payload: {
  profile: {
    email: string;
    firstName: string;
    lastName: string;
  };
  phone: string;
  password: string;
}): Promise<T> => {
  const instance = createInstanceProxy({
    isV2: true
  });
  const { data } = await instance.post<T>('/auth/signup', payload);
  return data;
};

export const getOrganizationBySlug = async <T>(
  slug: string
): Promise<{ data: T }> => {
  const instance = createInstanceProxy();
  const response = await instance.get<{ data: T }>(`/organizations/${slug}`);
  return response.data;
};

export const getAccountEvents = async <T>(query: {
  startDate?: string;
  endDate?: string;
}): Promise<{ data: T }> => {
  const instance = createInstanceProxy();
  const response = await instance.get<{ data: T }>(`/account/events`, {
    params: query
  });
  return response.data;
};

export const deleteAccount = async (): Promise<DeleteAccountResponse> => {
  const instance = createInstanceProxy();
  const response = await instance.delete<DeleteAccountResponse>('/account');
  return response.data;
};
